import { ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import ProjectService from "../services/projectService.js";
import { useProjectsList } from "./useProjectsList.js";

// Module-level so re-opening the panel doesn't lose scroll/pagination state
const currentProject = ref(null);
const docs = ref([]);
const isLoadingDocs = ref(false);
const isLoadingMoreDocs = ref(false);
const hasMoreDocs = ref(true);
let docsCursor = null;
let loadedForProjectId = null;
let hasWatcherStarted = false;
let lastDocsSortBy = "DATE_ADDED";
let lastDocsSortDirection = "DESC";

const projectService = new ProjectService();

export function useProjectsPanel() {
  const route = useRoute();
  const router = useRouter();
  const { projects, isLoading: isLoadingProjects, isLoadingMore: isLoadingMoreProjects, hasMore: hasMoreProjects, ensureLoaded: ensureProjectsLoaded, loadMore: loadMoreProjects, resort: resortProjects } = useProjectsList();

  // Viewing a specific project (or a document inside one) — panel shows that project's docs.
  // Otherwise (projects list, home, docs, etc.) — panel shows the all-projects browsing list.
  const currentProjectId = computed(() => {
    const inProjectContext = route.name === "v2-project" || route.name === "v2-document";
    return inProjectContext ? route.params.projectId ?? null : null;
  });

  const starredDocs = computed(() => docs.value.filter((d) => d.isFavorite));
  const otherDocs = computed(() => docs.value.filter((d) => !d.isFavorite));

  async function loadProjectInfo(projectId) {
    const fromList = projects.value.find((p) => p.id === projectId);
    if (fromList) currentProject.value = fromList;
    const fresh = await projectService.getProject({ projectId });
    if (fresh?.id) currentProject.value = fresh;
  }

  async function loadDocsFirstPage(projectId) {
    if (loadedForProjectId === projectId) return; // already loaded for this project
    loadedForProjectId = projectId;
    docs.value = [];
    docsCursor = null;
    hasMoreDocs.value = true;
    isLoadingDocs.value = true;
    await loadMoreDocs(projectId);
    isLoadingDocs.value = false;
  }

  async function loadMoreDocs(projectId) {
    const pid = projectId ?? currentProjectId.value;
    if (!pid || !hasMoreDocs.value || isLoadingMoreDocs.value) return;
    isLoadingMoreDocs.value = true;
    try {
      const res = await projectService.getDocuments({
        projectId: pid,
        pageSize: 30,
        endCursor: docsCursor,
        sortBy: lastDocsSortBy,
        sortDirection: lastDocsSortDirection,
      });
      docs.value.push(...(res.documents ?? []));
      docsCursor = res.endCursor;
      hasMoreDocs.value = res.hasNextPage;
    } finally {
      isLoadingMoreDocs.value = false;
    }
  }

  // Sort is applied server-side, so changing it re-fetches this project's
  // docs from the first page.
  async function resortDocs(sortBy, sortDirection) {
    lastDocsSortBy = sortBy;
    lastDocsSortDirection = sortDirection;
    const projectId = currentProjectId.value;
    if (!projectId) return;
    loadedForProjectId = null; // force loadDocsFirstPage to actually reload
    await loadDocsFirstPage(projectId);
  }

  // Lazily start reacting to route changes only once the panel has actually
  // been opened — avoids fetching anything while it's just mounted-but-hidden.
  function activate() {
    if (hasWatcherStarted) return;
    hasWatcherStarted = true;
    watch(
      currentProjectId,
      (projectId) => {
        if (projectId) {
          loadProjectInfo(projectId);
          loadDocsFirstPage(projectId);
        } else {
          ensureProjectsLoaded();
        }
      },
      { immediate: true }
    );
  }

  function openProject(id) {
    router.push({ name: "v2-project", params: { projectId: id } });
  }

  return {
    currentProjectId,
    currentProject,
    // "Not inside a project" — browse all projects
    projects,
    isLoadingProjects,
    isLoadingMoreProjects,
    hasMoreProjects,
    loadMoreProjects,
    ensureProjectsLoaded,
    resortProjects,
    openProject,
    // "Inside a project" — that project's documents
    starredDocs,
    otherDocs,
    isLoadingDocs,
    isLoadingMoreDocs,
    hasMoreDocs,
    loadMoreDocs,
    resortDocs,
    activate,
  };
}
